import * as React from 'react';
import { isHovering2 } from '@react-financial-charts/interactive/lib/components/index';
import { getMouseCanvas, GenericChartComponent, isDefined } from 'react-financial-charts';
import type { Levels } from '@/types/commontypes';
import type { MoreProps } from '@/types/commontypes';
import { getFillStyleWithOpacity } from '@/utils/rgbTorgba';
// eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
const ChartComponent = GenericChartComponent as unknown as React.ComponentType<Record<string, unknown>>;

export interface GannBoxProps {
  readonly startXY: number[];
  readonly defaultClassName?: string;
  readonly endXY: number[];
  readonly fontFamily: string;
  readonly fontSize: number;
  readonly fontFill: string;
  readonly interactiveCursorClass?: string;
  readonly onDragStart?: (e: React.MouseEvent, moreProps: MoreProps) => void;
  readonly onDrag?: (e: React.MouseEvent, moreProps: MoreProps) => void;
  readonly onDragComplete?: (e: React.MouseEvent, moreProps: MoreProps) => void;
  readonly onHover?: (e: React.MouseEvent, moreProps: MoreProps) => void;
  readonly onUnHover?: (e: React.MouseEvent, moreProps: MoreProps) => void;
  readonly selected: boolean;
  readonly strokeStyle: string;
  readonly strokeWidth: number;
  readonly tolerance: number;

  readonly priceLevels: Levels[];
  readonly timeLevels: Levels[];
  readonly onClick?: (e: React.MouseEvent, moreProps: MoreProps) => void;
  readonly onMouseDown?: (e: React.MouseEvent, moreProps: MoreProps) => void;

  readonly timeBgOpacity?: number;
  readonly priceBgOpacity?: number;
  readonly onDoubleClick?: (e: React.MouseEvent, moreProps: MoreProps) => void;
}

export class GannBox extends React.Component<GannBoxProps> {
  public static defaultProps = {
    strokeWidth: 1,
    tolerance: 4,
    selected: false,
  };

  public render() {
    const { selected, interactiveCursorClass } = this.props;
    const { onDragStart, onDrag, onDragComplete, onHover, onUnHover, onDoubleClick } = this.props;
    return (
      <ChartComponent
        isHover={this.isHover}
        canvasToDraw={getMouseCanvas}
        canvasDraw={this.drawOnCanvas}
        interactiveCursorClass={interactiveCursorClass}
        selected={selected}
        onDragStart={onDragStart}
        onDrag={onDrag}
        onDragComplete={onDragComplete}
        onHover={onHover}
        onUnHover={onUnHover}
        drawOn={['mousemove', 'mouseleave', 'pan', 'drag']}
        onClick={this.handleClick}
        onMouseDown={this.handleMouseDown}
        onDoubleClick={onDoubleClick}
      />
    );
  }

  private readonly drawOnCanvas = (ctx: CanvasRenderingContext2D, moreProps: MoreProps) => {
    const { strokeWidth, fontFamily, fontSize, priceLevels, timeLevels, timeBgOpacity, priceBgOpacity } = this.props;

    const lines = this.helper(this.props, moreProps);
    ctx.lineWidth = strokeWidth;
    ctx.font = `${fontSize}px ${fontFamily}`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    const horizontalLines = lines.filter(line => line.y1 === line.y2);
    const verticalLines = lines.filter(line => line.x1 === line.x2);

    const visiblePriceLines = horizontalLines;
    const visibleTimeLines = verticalLines;

    visiblePriceLines.forEach((line, index) => {
      const color = priceLevels.filter(pl => pl.show)[index]?.color || 'black';
      const finalFillStyle = getFillStyleWithOpacity(line.color, priceBgOpacity || 0);
      ctx.strokeStyle = line.color;
      ctx.fillStyle = finalFillStyle;
      ctx.beginPath();
      ctx.moveTo(line.x1, line.y1);
      ctx.lineTo(line.x2, line.y2);
      ctx.stroke();

      line.labels.forEach(label => {
        ctx.fillStyle = color;
        ctx.fillText(String(label.text), label.x, label.y);
      });
    });

    visibleTimeLines.forEach((line, index) => {
      const color = timeLevels.filter(tl => tl.show)[index]?.color || 'black';
      ctx.strokeStyle = color;
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.moveTo(line.x1, line.y1);
      ctx.lineTo(line.x2, line.y2);
      ctx.stroke();

      line.labels.forEach(label => {
        ctx.fillStyle = color;
        ctx.fillText(String(label.text), label.x, label.y);
      });
    });

    const shownPriceLevels = priceLevels.filter(pl => pl.show);
    for (let i = 0; i < visiblePriceLines.length - 1; i++) {
      const line1 = visiblePriceLines[i];
      const line2 = visiblePriceLines[i + 1];
      const nextColor = getFillStyleWithOpacity(shownPriceLevels[i + 1]?.color, priceBgOpacity);
      if (nextColor) {
        ctx.fillStyle = nextColor;
        ctx.beginPath();
        ctx.moveTo(line1.x1, line1.y1);
        ctx.lineTo(line1.x2, line1.y2);
        ctx.lineTo(line2.x2, line2.y2);
        ctx.lineTo(line2.x1, line2.y1);
        ctx.closePath();
        ctx.fill();
      }
    }

    const shownTimeLevels = timeLevels.filter(tl => tl.show);
    for (let i = 0; i < visibleTimeLines.length - 1; i++) {
      const line1 = visibleTimeLines[i];
      const line2 = visibleTimeLines[i + 1];
      const nextColor = getFillStyleWithOpacity(shownTimeLevels[i + 1]?.color, timeBgOpacity);
      if (nextColor) {
        ctx.fillStyle = nextColor;
        ctx.beginPath();
        ctx.moveTo(line1.x1, line1.y1);
        ctx.lineTo(line2.x1, line2.y1);
        ctx.lineTo(line2.x2, line2.y2);
        ctx.lineTo(line1.x2, line1.y2);
        ctx.closePath();
        ctx.fill();
      }
    }
  };
  private readonly handleClick = (e: React.MouseEvent, moreProps: MoreProps) => {
    const isHovered = this.isHover(moreProps);
    if (isHovered && this.props.onClick) {
      this.props.onClick(e, moreProps);
    }
  };
  private readonly handleMouseDown = (e: React.MouseEvent, moreProps: MoreProps) => {
    const isHovered = this.isHover(moreProps);
    if (isHovered && this.props.onMouseDown) {
      this.props.onMouseDown(e, moreProps);
    }
  };
  private readonly isHover = (moreProps: MoreProps) => {
    const { mouseXY, currentItem } = moreProps;
    const { tolerance = 4 } = this.props;

    // Ensure the mouse is over a visible candle area
    if (!mouseXY || !currentItem) return false;

    const lines = this.helper(this.props, moreProps);

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      if (isHovering2([line.x1, line.y1], [line.x2, line.y2], mouseXY, tolerance)) {
        return true;
      }
    }
    return false;
  };

  private readonly helper = (props: GannBoxProps, moreProps: MoreProps) => {
    const { startXY, endXY, timeLevels, priceLevels } = props;
    const { xScale, chartConfig } = moreProps;
    const { yScale } = chartConfig;

    if (!isDefined(startXY) || !isDefined(endXY)) {
      return [];
    }

    const [x1, y1] = startXY;
    const [x2, y2] = endXY;

    const dx = x2 - x1;
    const dy = y2 - y1;

    const lines = [];

    // **Draw Horizontal Price Levels**
    if (priceLevels) {
      for (let i = 0; i < priceLevels.length; i++) {
        const level = priceLevels[i];
        if (level.show) {
          const y = y1 + dy * level.value;
          const isLeftToRight = x1 > x2;
          const x1Pos = isLeftToRight ? xScale(x1) + 15 : xScale(x2) + 15;
          const x2Pos = isLeftToRight ? xScale(x2) - 15 : xScale(x1) - 15;
          lines.push({
            x1: xScale(x1),
            y1: yScale(y),
            x2: xScale(x2),
            y2: yScale(y),
            color: level.color,
            labels: [
              { x: x1Pos, y: yScale(y), text: level.value, align: 'right' },
              { x: x2Pos, y: yScale(y), text: level.value, align: 'left' },
            ],
          });
        }
      }
    }

    // **Draw Vertical Time Levels**
    if (timeLevels) {
      for (let i = 0; i < timeLevels.length; i++) {
        const level = timeLevels[i];
        if (level.show) {
          const x = x1 + dx * level.value;
          const isTopToBottom = y1 < y2;
          const y1Pos = isTopToBottom ? yScale(y1) + 12 : yScale(y2) + 12;
          const y2Pos = isTopToBottom ? yScale(y2) - 12 : yScale(y1) - 12;
          lines.push({
            x1: xScale(x),
            y1: yScale(y1),
            x2: xScale(x),
            y2: yScale(y2),
            color: level.color,
            labels: [
              { x: xScale(x), y: y1Pos, text: level.value, align: 'bottom' },
              { x: xScale(x), y: y2Pos, text: level.value, align: 'top' },
            ],
          });
        }
      }
    }
    return lines;
  };
}
